'use client';

import * as React from 'react';
import { type FC, useCallback } from 'react';
import type { FreeGiftsData } from '../../../../types/front.graphql';
import { observer, useObservable } from '@legendapp/state/react';
// Import from gift-data mediator (has quantityStepper logic), not from gifts-partials
import { GiftData } from '../gift-data/gift-data';
import type { ProductGiftContentsProps } from './product-gift-contents.types';
import type { SelectedGift } from '../../types/gift-selection.types';
import styles from './product-gift-contents.module.css';
import { cn } from '@kidslabs/mario-nextjs-react-ui-core';

export const ProductGiftContents: FC<ProductGiftContentsProps> = observer(
    ({
        FlatListComponent,
        showOnlyRemainingGifts,
        counterValue = 0,
        selectedGifts$ = undefined,
        sortedGiftsDataList = [],
        onSelectGift,
        remainingProductQuantity = 0,
        isReplacementMode = false,
        sessionId,
        containerClassName,
        layout,
        enableAutoFill,
        onAutoFillRequired,
    }) => {
        const selectedGiftsObservable = selectedGifts$?.get?.();
        // ✅ FIX: Wrap selectedGiftsArray in useMemo to prevent dependencies changing on every render
        const selectedGiftsArray = React.useMemo(() => {
            return selectedGiftsObservable ?? [];
        }, [selectedGiftsObservable]);

        // ✅ FIX: Calculate remaining quantity for second selection
        const remainingQuantityForSecondSelection = remainingProductQuantity;

        // ✅ FIX: Get rules that haven't been selected yet
        const remainingGiftItems = React.useMemo(() => {
            if (!showOnlyRemainingGifts) {
                return sortedGiftsDataList;
            }

            // 🎁 REPLACEMENT MODE: Skip all filtering, show all gifts of the rule
            if (isReplacementMode) {
                console.log('🔄 [ProductGiftContents] Replacement mode: showing all gifts without filtering');
                return sortedGiftsDataList;
            }

            // NOTE: enableAutoFill only applies in round 1 (showOnlyRemainingGifts=false),
            // which is already handled above. In round 2, always filter by remaining quantity.

            // Get rule IDs that are already selected
            //const selectedRuleIds = new Set(selectedGiftsArray.map(gift => gift.ruleId));

            // Filter out already selected rules and check eligibility with remaining quantity
            return sortedGiftsDataList.filter(giftData => {
                //const ruleId = giftData.rule?.rule_id;
                const buyRequirement = giftData.rule?.discount_step || 1;

                // Rule not selected AND remaining quantity can satisfy buy requirement
                //const isNotSelected = !selectedRuleIds.has(ruleId ?? 0);
                return remainingQuantityForSecondSelection >= buyRequirement; //isNotSelected && canAfford;
            });
        }, [showOnlyRemainingGifts, sortedGiftsDataList, remainingQuantityForSecondSelection, isReplacementMode]);

        const productGiftRules = React.useMemo(() => {
            return showOnlyRemainingGifts ? remainingGiftItems : sortedGiftsDataList;
        }, [showOnlyRemainingGifts, remainingGiftItems, sortedGiftsDataList]);

        // These will be tracked by Memo and trigger re-renders when changed
        const productQuantity$ = useObservable(
            showOnlyRemainingGifts ? remainingQuantityForSecondSelection : counterValue
        );
        const selectionRound$ = useObservable(showOnlyRemainingGifts ? 2 : 1);

        React.useEffect(() => {
            const newQuantity = showOnlyRemainingGifts ? remainingQuantityForSecondSelection : counterValue;
            productQuantity$?.set(newQuantity);
            console.log('📊 productQuantity$ updated:', newQuantity);
        }, [showOnlyRemainingGifts, remainingQuantityForSecondSelection, counterValue, productQuantity$]);

        React.useEffect(() => {
            const newRound = showOnlyRemainingGifts ? 2 : 1;
            selectionRound$?.set(newRound);
            console.log('🔢 selectionRound$ updated:', newRound);
        }, [showOnlyRemainingGifts, selectionRound$]);

        // ✅ FIX: Enhanced onSelectGift to preserve previous selections
        const enhancedOnSelectGift = useCallback(
            (newGift: SelectedGift) => {
                // For second selection, we add to existing selections instead of replacing
                if (showOnlyRemainingGifts && selectedGiftsArray.length > 0) {
                    console.log('➕ Adding to existing selections (second phase)', newGift);
                    // This is second selection - add to existing gifts
                    onSelectGift(newGift); // The hook will handle merging correctly
                } else {
                    console.log('🔄 First selection or full list mode');
                    // This is first selection or full list mode
                    onSelectGift(newGift);
                }
            },
            [onSelectGift, selectedGiftsArray, showOnlyRemainingGifts]
        );

        const keyExtractor = React.useCallback((item: FreeGiftsData) => `${item.rule?.rule_id}`, []);

        // Memoized renderGiftItem to avoid unnecessary re-creation
        const renderGiftRuleItem = useCallback(
            (info: { item: FreeGiftsData }) => {
                const { item } = info;
                return (
                    <GiftData
                        layout={layout}
                        giftData={item}
                        title={item.rule?.name ?? ''}
                        onSelectGift={enhancedOnSelectGift}
                        selectedGifts$={selectedGifts$}
                        productQuantity$={productQuantity$}
                        selectionRound$={selectionRound$}
                        sessionId={sessionId}
                        enableAutoFill={enableAutoFill}
                        onAutoFillRequired={onAutoFillRequired}
                    />
                );
            },
            [
                enhancedOnSelectGift,
                selectedGifts$,
                productQuantity$,
                selectionRound$,
                sessionId,
                layout,
                enableAutoFill,
                onAutoFillRequired,
            ]
        );

        const MarginDivider = () => <div className={styles.divider} />;

        if (productGiftRules.length === 0) {
            return null;
        }

        // Use FlatListComponent if provided, otherwise use simple map
        if (FlatListComponent) {
            return (
                <FlatListComponent
                    testID="flatlist-rule-item"
                    data={productGiftRules}
                    renderItem={renderGiftRuleItem}
                    keyExtractor={keyExtractor}
                    contentContainerStyle={{
                        paddingTop: 20,
                        paddingBottom: 20,
                        overflow: 'hidden',
                    }}
                    ItemSeparatorComponent={MarginDivider}
                />
            );
        }

        return (
            <div className={cn(styles.container, containerClassName)} data-testid="flatlist-rule-item">
                {productGiftRules.map((item, index) => (
                    <React.Fragment key={`${item.rule?.rule_id}`}>
                        {index > 0 && <MarginDivider />}
                        <GiftData
                            layout={layout}
                            giftData={item}
                            title={item.rule?.name ?? ''}
                            onSelectGift={enhancedOnSelectGift}
                            selectedGifts$={selectedGifts$}
                            productQuantity$={productQuantity$}
                            selectionRound$={selectionRound$}
                            sessionId={sessionId}
                            enableAutoFill={enableAutoFill}
                            onAutoFillRequired={onAutoFillRequired}
                        />
                    </React.Fragment>
                ))}
            </div>
        );
    }
);
